import { useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import { Annotation, EditorSelection, EditorState, Prec } from "@codemirror/state";
import { EditorView, highlightActiveLine, keymap, lineNumbers } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentLess, indentMore, toggleComment } from "@codemirror/commands";
import { indentUnit } from "@codemirror/language";
import { testIds } from "@/testIds";
import {
  COLOUR_FOOTNOTE,
  colourNote,
  colourPrefixBefore,
  matchColourNames,
  type ColourName,
  type FigureColours,
} from "@/lib/tikzAuthoring";
import {
  coloursField,
  inlineErrorField,
  lineHighlightField,
  minimalChange,
  setLineHighlightEffect,
  setColoursEffect,
  setInlineErrorEffect,
  swatchPlugin,
  tikzEditorTheme,
  tikzHighlighting,
  type InlineErrorSpec,
} from "./tikzCodeMirror";
import styles from "./TikzSplitView.module.scss";

const T = testIds.tikzSplit;

/** A write that came from outside the editor (the document's undo, a merge). */
const external = Annotation.define<boolean>();

/** How long after Esc a Tab leaves the editor (CodeMirror's own convention). */
export const ESC_TAB_WINDOW_MS = 2000;

export interface TikzSourceEditorHandle {
  /** Replace the whole source as one undoable step (a template). */
  replaceAll: (text: string) => void;
  /**
   * Take a kept Ask Lilia change: only what differs, as one undoable step,
   * the caret at the end of `caretLine` (1-based).
   */
  applyChange: (text: string, caretLine?: number) => void;
  /** Bring a line into view (an Explain chip was clicked). */
  scrollToLine: (line: number) => void;
  focus: () => void;
  view: () => EditorView | null;
}

interface Completion {
  from: number;
  to: number;
  items: ColourName[];
  index: number;
}

export interface TikzSourceEditorProps {
  value: string;
  onChange: (next: string) => void;
  colours: FigureColours;
  error: InlineErrorSpec | null;
  /** Lines an Ask Lilia chip points at (1c), or null. */
  highlight?: { from: number; to: number } | null;
  /** ⌘S */
  onDrawNow: () => void;
  /** Esc, Esc */
  onDone: () => void;
  /** Esc then Tab: focus goes on past the editor. */
  onLeave: () => void;
  /** The first Esc: the footer says what the next key does. */
  onEscArmed?: (armed: boolean) => void;
  handle?: Ref<TikzSourceEditorHandle>;
  /**
   * A plot that follows its table (step 4a): the source can be read, selected
   * and copied, but an edit is held back and handed over with a way to apply it
   * (Detach keeps the keystroke).
   */
  locked?: boolean;
  onLockedEdit?: (apply: () => void) => void;
}

/**
 * The source pane's editor: line numbers, TikZ highlighting, swatches, the
 * error at its line, and the `lilia-` colour list. Keys: ⌘S draws now, ⌘/
 * comments, Tab indents two spaces, Esc then Tab leaves, Esc twice is Done.
 */
export function TikzSourceEditor(props: TikzSourceEditorProps) {
  const host = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const [completion, setCompletion] = useState<Completion | null>(null);
  const completionRef = useRef<Completion | null>(null);
  completionRef.current = completion;
  const escArmedUntil = useRef(0);

  // Callbacks change every render; the editor is built once and reads them here.
  const cb = useRef(props);
  cb.current = props;

  const setArmed = (armed: boolean) => {
    const was = escArmedUntil.current > Date.now();
    escArmedUntil.current = armed ? Date.now() + ESC_TAB_WINDOW_MS : 0;
    if (was !== armed) cb.current.onEscArmed?.(armed);
  };

  const pick = (c: Completion, i: number) => {
    const v = viewRef.current;
    const item = c.items[i];
    if (!v || !item) return;
    v.dispatch({
      changes: { from: c.from, to: c.to, insert: item.name },
      selection: EditorSelection.cursor(c.from + item.name.length),
      userEvent: "input.complete",
    });
    setCompletion(null);
    v.focus();
  };

  useEffect(() => {
    if (!host.current) return;
    const completionKeys = Prec.highest(
      keymap.of([
        {
          key: "ArrowDown",
          run: () => {
            const c = completionRef.current;
            if (!c) return false;
            setCompletion({ ...c, index: (c.index + 1) % c.items.length });
            return true;
          },
        },
        {
          key: "ArrowUp",
          run: () => {
            const c = completionRef.current;
            if (!c) return false;
            setCompletion({ ...c, index: (c.index - 1 + c.items.length) % c.items.length });
            return true;
          },
        },
        {
          key: "Enter",
          run: () => {
            const c = completionRef.current;
            if (!c) return false;
            pick(c, c.index);
            return true;
          },
        },
        {
          key: "Tab",
          run: () => {
            const c = completionRef.current;
            if (!c) return false;
            pick(c, c.index);
            return true;
          },
        },
      ]),
    );
    const figureKeys = Prec.high(
      keymap.of([
        {
          key: "Mod-s",
          preventDefault: true,
          run: () => {
            cb.current.onDrawNow();
            return true;
          },
        },
        { key: "Mod-/", run: toggleComment },
        {
          key: "Escape",
          run: () => {
            if (completionRef.current) {
              setCompletion(null);
              return true;
            }
            if (escArmedUntil.current > Date.now()) {
              setArmed(false);
              cb.current.onDone();
              return true;
            }
            setArmed(true);
            return true;
          },
        },
        {
          key: "Tab",
          run: (view) => {
            if (escArmedUntil.current > Date.now()) {
              setArmed(false);
              cb.current.onLeave();
              return true;
            }
            if (view.state.selection.ranges.some((r) => !r.empty)) return indentMore(view);
            view.dispatch(view.state.replaceSelection("  "), { userEvent: "input.indent", scrollIntoView: true });
            return true;
          },
          shift: (view) => {
            if (escArmedUntil.current > Date.now()) {
              setArmed(false);
              return false;
            }
            return indentLess(view);
          },
        },
      ]),
    );

    const view = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: cb.current.value,
        extensions: [
          lineNumbers(),
          highlightActiveLine(),
          history(),
          indentUnit.of("  "),
          EditorState.tabSize.of(2),
          completionKeys,
          figureKeys,
          keymap.of([...defaultKeymap, ...historyKeymap]),
          tikzHighlighting,
          coloursField,
          swatchPlugin,
          inlineErrorField,
          lineHighlightField,
          tikzEditorTheme,
          EditorState.transactionFilter.of((tr) => {
            if (!cb.current.locked || !tr.docChanged || tr.annotation(external)) return tr;
            const changes = tr.changes;
            const before = tr.startState.doc;
            cb.current.onLockedEdit?.(() => {
              const v = viewRef.current;
              // Only onto the text it was typed into.
              if (v && v.state.doc.eq(before)) v.dispatch({ changes, userEvent: "input.type", scrollIntoView: true });
            });
            return [];
          }),
          EditorView.contentAttributes.of({ "aria-label": "TikZ source", spellcheck: "false" }),
          EditorView.domEventHandlers({
            keydown: (e) => {
              // Any key but Esc (and modifiers) disarms Esc-then-Tab.
              if (e.key !== "Escape" && e.key !== "Tab" && !["Shift", "Meta", "Control", "Alt"].includes(e.key)) {
                if (escArmedUntil.current) setArmed(false);
              }
              return false;
            },
            blur: () => {
              setCompletion(null);
              if (escArmedUntil.current) setArmed(false);
              return false;
            },
          }),
          EditorView.updateListener.of((u) => {
            const ext = u.transactions.some((t) => t.annotation(external));
            if (u.docChanged && !ext) cb.current.onChange(u.state.doc.toString());
            // The colour list: open while typing a lilia- name, closed when the caret leaves it.
            if (!u.docChanged && !u.selectionSet) return;
            const sel = u.state.selection.main;
            const typing = u.transactions.some((t) => t.isUserEvent("input.type") || t.isUserEvent("delete"));
            if (!sel.empty || ext) {
              if (completionRef.current) setCompletion(null);
              return;
            }
            const line = u.state.doc.lineAt(sel.head);
            const prefix = colourPrefixBefore(line.text.slice(0, sel.head - line.from));
            const items = prefix ? matchColourNames(prefix) : [];
            if (!prefix || !items.length || (!typing && !completionRef.current)) {
              if (completionRef.current) setCompletion(null);
              return;
            }
            setCompletion({ from: sel.head - prefix.length, to: sel.head, items, index: 0 });
          }),
        ],
      }),
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Built once; value, colours and the error are pushed in below.
  }, []);

  // A source changed from outside (the document's undo, a merge): put it in,
  // quietly, replacing only what differs so the cursor keeps its place.
  useEffect(() => {
    const v = viewRef.current;
    if (!v) return;
    const change = minimalChange(v.state.doc.toString(), props.value);
    if (!change) return;
    v.dispatch({ changes: change, annotations: external.of(true) });
  }, [props.value]);

  useEffect(() => {
    viewRef.current?.dispatch({ effects: setColoursEffect.of(props.colours) });
  }, [props.colours]);

  useEffect(() => {
    viewRef.current?.dispatch({ effects: setInlineErrorEffect.of(props.error) });
  }, [props.error]);

  const highlight = props.highlight ?? null;
  useEffect(() => {
    viewRef.current?.dispatch({ effects: setLineHighlightEffect.of(highlight) });
    // A new object with the same lines is the same highlight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight?.from, highlight?.to]);

  useImperativeHandle(
    props.handle,
    () => ({
      replaceAll: (text: string) => {
        const v = viewRef.current;
        if (!v) return;
        v.dispatch({
          changes: { from: 0, to: v.state.doc.length, insert: text },
          selection: EditorSelection.cursor(0),
          userEvent: "input.template",
          scrollIntoView: true,
        });
      },
      applyChange: (text: string, caretLine?: number) => {
        const v = viewRef.current;
        if (!v) return;
        const change = minimalChange(v.state.doc.toString(), text);
        const doc = change ? v.state.update({ changes: change }).state.doc : v.state.doc;
        const line = doc.line(Math.min(Math.max(1, caretLine ?? 1), doc.lines));
        v.dispatch({
          ...(change ? { changes: change } : {}),
          selection: EditorSelection.cursor(line.to),
          userEvent: "input.ai",
          scrollIntoView: true,
        });
      },
      scrollToLine: (n: number) => {
        const v = viewRef.current;
        if (!v) return;
        const line = v.state.doc.line(Math.min(Math.max(1, n), v.state.doc.lines));
        v.dispatch({ effects: EditorView.scrollIntoView(line.from, { y: "center" }) });
      },
      focus: () => viewRef.current?.focus(),
      view: () => viewRef.current,
    }),
    [],
  );

  // The list sits under the name being typed (top-left when there's no layout, as in tests).
  let pos = { top: 0, left: 0 };
  if (completion && viewRef.current && wrap.current) {
    try {
      const c = viewRef.current.coordsAtPos(completion.from);
      const r = wrap.current.getBoundingClientRect();
      if (c) pos = { top: c.bottom - r.top + 4, left: Math.max(0, c.left - r.left - 18) };
    } catch {
      /* no layout */
    }
  }

  return (
    <div ref={wrap} className={styles.editorWrap}>
      <div ref={host} className={styles.editorHost} />
      {completion && (
        <div
          className={styles.autocomplete}
          data-testid={T.colourAutocomplete}
          role="listbox"
          aria-label="Theme colours"
          style={{ top: pos.top, left: pos.left }}
        >
          {completion.items.map((item, i) => {
            const colour = props.colours[item.name];
            return (
              <div
                key={item.name}
                role="option"
                aria-selected={i === completion.index}
                data-testid={T.colourOption}
                data-colour-name={item.name}
                className={styles.acRow}
                // mousedown, so the editor keeps focus.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(completion, i);
                }}
              >
                <span
                  className={styles.acSwatch}
                  data-colour={colour ?? ""}
                  style={{ background: colour ?? "transparent" }}
                  aria-hidden="true"
                />
                <span className={styles.acName}>{item.name}</span>
                <span className={styles.acNote}>{colourNote(item, props.colours)}</span>
              </div>
            );
          })}
          <div className={styles.acFoot}>{COLOUR_FOOTNOTE}</div>
        </div>
      )}
    </div>
  );
}
